import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiGet } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

interface SubSellerItem {
  id: string;
  parentId: string;
  name: string;
  username: string | null;
  level: number;
  payinSharePercent: number;
  payoutSharePercent: number;
}

function sanitizeForUsername(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function SubSellerCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [payinSharePercent, setPayinSharePercent] = useState('');
  const [payoutSharePercent, setPayoutSharePercent] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');

  // Get existing sub-sellers for parent selection
  const { data: subSellersData } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () => apiGet<{ items: SubSellerItem[]; total: number }>('/seller/sub-sellers'),
  });

  const subSellers = subSellersData?.ok ? subSellersData.data.items : [];
  // Only level 1 and 2 can be parents (max depth 3)
  const possibleParents = subSellers.filter((ss) => ss.level < 3);

  // Build the username chain: <seller>-<…parent chain…>-<sanitized name>.
  // When parentId is empty, the seller itself is the parent (level 1).
  // When parentId is a sub-seller's id, walk up its parent chain to assemble usernames.
  useEffect(() => {
    if (usernameTouched) return;
    const namePart = sanitizeForUsername(name);

    // Always seed the seller's username (then parent chain) as the default
    // prefix — visible even before a name is typed.
    const parts: string[] = [];
    if (user?.username) parts.push(sanitizeForUsername(user.username));

    if (parentId) {
      // Walk up the sub-seller chain
      const byId = new Map(subSellers.map((s) => [s.id, s]));
      const ancestors: string[] = [];
      let cursor = byId.get(parentId);
      while (cursor) {
        if (cursor.username) ancestors.unshift(sanitizeForUsername(cursor.username));
        // Stop when parent is the seller (parentId equals seller's _id, not in this map)
        cursor = byId.get(cursor.parentId);
      }
      parts.push(...ancestors);
    }

    if (namePart) parts.push(namePart);
    setUsername(parts.filter(Boolean).join('-'));
  }, [name, parentId, user?.username, subSellers, usernameTouched]);

  const parentOptions = [
    { value: '', label: t('seller.directSubSeller') },
    ...possibleParents.map((ss) => ({
      value: ss.id,
      label: `${'  '.repeat(ss.level - 1)}${ss.name} (${ss.payinSharePercent}% / ${ss.payoutSharePercent}%)`,
    })),
  ];

  const createMutation = useMutation({
    mutationFn: () =>
      apiPost('/seller/sub-sellers', {
        parentId: parentId || user?.sellerId || '',
        name,
        username,
        password,
        payinSharePercent: parseFloat(payinSharePercent) || 0,
        payoutSharePercent: parseFloat(payoutSharePercent) || 0,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
        navigate('/seller/sub-sellers');
      } else {
        setError(res.error.message);
      }
    },
    onError: (err) => {
      setError((err as Error).message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    createMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{t('seller.createSubSeller')}</h1>

      <Card className="max-w-xl">
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}

            <Input
              label={t('sellers.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <Input
              label={t('auth.username')}
              value={username}
              onChange={(e) => {
                setUsernameTouched(true);
                setUsername(e.target.value);
              }}
              required
            />

            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payinFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payinSharePercent}
              onChange={(e) => setPayinSharePercent(e.target.value)}
              required
            />

            <Input
              label={`${t('sellers.payoutFee')} (%)`}
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={payoutSharePercent}
              onChange={(e) => setPayoutSharePercent(e.target.value)}
              required
            />

            <Select
              label={t('seller.parentSeller')}
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              options={parentOptions}
            />

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/sub-sellers')}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
